/**
 * EmailVerifyBanner — plan 029 top-of-shell nudge that surfaces when the
 * signed-in parent hasn't verified their email address.
 *
 * Signup already enqueued a fresh 6-digit code inside the signup
 * transaction, so the banner defaults to the code-entry form rather than
 * a "Send code" first step — otherwise every new-account parent gets a
 * second email sent, invalidating the first one they were about to type.
 *
 * Errors from `/v1/auth/email/verify-confirm` are surfaced inline; none
 * of them log the parent out. The banner stays visible until the
 * `/v1/family/self` refresh flips `email_verified=true`.
 */

import { useEffect, useState } from 'preact/hooks';
import { authSession } from '../state/session';
import {
  familySelf,
  refreshFamilySelf,
  requestEmailVerifyCode,
  submitEmailVerifyCode,
} from '../data/family-self';
import { Button } from './Button';
import { Input } from './Input';
import { useVisiblePoll } from '../hooks/useVisiblePoll';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Extract a specific error string ("wrong_code", "code_expired", etc.)
 * from the api helper's serialised error message
 * (`"api 400: {\"detail\":\"wrong_code\"}"`) and render a friendly line.
 */
function friendlyError(raw: string): string {
  if (raw.includes('wrong_code')) {
    return "That code doesn't match. Check the digits or click Resend.";
  }
  if (raw.includes('code_expired')) {
    return 'That code has expired. Click Resend for a new one.';
  }
  if (raw.includes('no_pending_verification')) {
    return 'No verification is in progress. Click Resend to start a new one.';
  }
  if (raw.includes('too_many_attempts')) {
    return 'Too many wrong tries on this code. Click Resend to start over.';
  }
  if (raw.includes('email verify-start limit reached')) {
    return "You've asked for too many codes recently. Try again in an hour.";
  }
  return raw;
}

export function EmailVerifyBanner() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    void refreshFamilySelf();
  }, []);

  // Visible-only: a hidden tab has nobody to show a verification banner to.
  useVisiblePoll(() => {
    void refreshFamilySelf();
  }, REFRESH_INTERVAL_MS);

  if (authSession.value === null) return null;
  const self = familySelf.value;
  if (self === null) return null;
  if (self.email_verified) return null;
  const email = self.email;

  async function onResend(): Promise<void> {
    setErr(null);
    setInfo(null);
    setResendBusy(true);
    try {
      await requestEmailVerifyCode();
      setInfo(`New code sent to ${email}.`);
      setCode('');
    } catch (e) {
      setErr(friendlyError((e as Error).message));
    } finally {
      setResendBusy(false);
    }
  }

  async function onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      await submitEmailVerifyCode(code);
      // family-self refresh flips email_verified true; banner unmounts.
    } catch (e) {
      setErr(friendlyError((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="border-b border-amber-300 bg-amber-50 text-amber-900">
      <div class="mx-auto flex max-w-4xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="text-sm">
          <strong class="font-semibold">Verify your email</strong> &mdash; enter
          the 6-digit code we emailed to{' '}
          <span class="font-mono text-[13px]">{email}</span>. Verification is
          required to add endpoints or start billing.
        </div>
        <form
          onSubmit={(e) => void onSubmit(e)}
          class="flex items-center gap-2"
        >
          <Input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onInput={(e: Event) => {
              setCode((e.target as HTMLInputElement).value);
            }}
            placeholder="6-digit code"
            autocomplete="one-time-code"
            class="w-32 font-mono"
            required
          />
          <Button
            type="submit"
            size="sm"
            disabled={busy || resendBusy || code.length < 6}
          >
            {busy ? 'Verifying…' : 'Verify'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void onResend()}
            disabled={busy || resendBusy}
          >
            {resendBusy ? 'Sending…' : 'Resend'}
          </Button>
        </form>
      </div>
      {(err !== null || info !== null) && (
        <div class="mx-auto max-w-4xl px-4 pb-2 text-xs">
          {err !== null && (
            <span class="text-danger-500" role="alert">
              {err}
            </span>
          )}
          {err === null && info !== null && <span>{info}</span>}
        </div>
      )}
    </div>
  );
}
